import { Component, OnInit, OnDestroy, ElementRef, ViewChild, SecurityContext, Inject } from "@angular/core";
import { Subject } from "rxjs";
import { Environment } from "../models/environment";
import { EnvironmentService } from "../services/environment.service";
import { ReleaseReportService } from "../services/releasereport.service";
import { MatRadioChange } from "@angular/material/radio";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { FormBuilder, FormGroup } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { LoadingService } from "../services/loading.service";
import { finalize } from "rxjs/operators";

@Component({
    selector: 'app-releasenotes',
    templateUrl: './releasenotes.component.html',
    styleUrls: ['./releasenotes.component.css', '../home/home.component.css'],
    providers: [DatePipe],
    standalone: false
})
export class ReleaseNotesComponent implements OnInit, OnDestroy {
  releaseNotes: any = [];
  execSummary: any = [];
  releaseDates: Array<string> = [];
  friendlyDates: Array<string> = [];
  selectedDate: string = '';
  environment: Environment;
  mode: string = 'notes';
  @ViewChild('summary', { static: false }) summary!: ElementRef;
  private readonly _destroying$ = new Subject<void>();
  dateRangeForm: FormGroup;

  constructor(
    @Inject(EnvironmentService) private readonly environmentService: EnvironmentService,
    @Inject(ReleaseReportService) private readonly releaseReportService: ReleaseReportService,
    @Inject(DomSanitizer) private readonly sanitizer: DomSanitizer,
    @Inject(DatePipe) private readonly datePipe: DatePipe,
    @Inject(FormBuilder) private readonly fb: FormBuilder,
    public readonly loadingService: LoadingService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.dateRangeForm = this.fb.group({
      start: [null],
      end: [null]
    });
  }

  ngOnInit(): void {
    this.mode = 'notes';
    this.selectedDate = 'Select';
    this.getReleaseDates();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  clearDates(): void {
    this.selectedDate = '';
  }

  getReleaseDates(): void {
    this.loadingService.show('releaseDates');
    this.releaseReportService.getReleaseDates()
      .pipe(
        finalize(() => this.loadingService.hide('releaseDates'))
      )
      .subscribe(
        (response: Array<string>) => {
          if (response) {
            this.releaseDates.push(...(response));
            this.releaseDates.forEach(date => {
              this.friendlyDates.push(this.getFriendlyDate(date));
            });
          }
        }
      );
  }

  getFriendlyDate(unfriendlyDateString: string): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    const theDate: Date = new Date(unfriendlyDateString);
    return theDate.toLocaleDateString("en-US", options);
  }

  get reportTitle(): string {
    const reportTitleFormat = 'EEE. M/d/yyyy';
    if(this.dateRangeForm.value.start && this.dateRangeForm.value.end) {
      const formattedStartDate = this.datePipe.transform(this.dateRangeForm.value.start, reportTitleFormat);
      const formattedEndDate = this.datePipe.transform(this.dateRangeForm.value.end, reportTitleFormat);
      return `Release Dates - ${formattedStartDate} - ${formattedEndDate}`
    }
    return "Release Notes";
  }

  getReleaseNotes(startDateString?: string, endDateString?: string): void {
    this.releaseNotes = [];

    if (startDateString && startDateString !== '' && endDateString && endDateString !== '') {
      let newStartDate = new Date(startDateString);
      let startDate = this.formatDate(newStartDate);
      let newEndDate = new Date(endDateString);
      let endDate = this.formatDate(newEndDate);

      this.loadingService.show('releaseNotes');
      this.releaseReportService.getReleaseNotes(startDate, endDate)
        .pipe(
          finalize(() => this.loadingService.hide('releaseNotes'))
        )
        .subscribe((releaseNotes: any) => {
          this.releaseNotes = releaseNotes;
        });
    }
  }

  getExecutiveSummary(startDateString?: string, endDateString?: string): void {
    this.releaseNotes = [];

    if (startDateString && startDateString !== '' && endDateString && endDateString !== '') {
      let newStartDate = new Date(startDateString);
      let startDate = this.formatDate(newStartDate);
      let newEndDate = new Date(endDateString);
      let endDate = this.formatDate(newEndDate);

      this.loadingService.show('executiveSummary');
      this.releaseReportService.getExecutiveSummary(startDate, endDate)
        .pipe(
          finalize(() => this.loadingService.hide('executiveSummary'))
        )
        .subscribe((execSummary: any) => {
          this.execSummary = execSummary;
        });
    }
  }

  handleModeChange(data: MatRadioChange): void {
    this.mode = data.value;
    let startDate = this.dateRangeForm.get('start')?.value;
    let endDate = this.dateRangeForm.get('end')?.value;
    if (this.mode === 'notes') {
      this.getReleaseNotes(startDate, endDate);
    } else {
      this.getExecutiveSummary(startDate, endDate);
    }
  }

  handleDateChange(startDate?: string, endDate? : string): void {
    if (this.mode === 'notes') {
      this.getReleaseNotes(startDate, endDate);
    } else {
      this.getExecutiveSummary(startDate, endDate);
    }
  }

  formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
    return date.toLocaleDateString('en-US', options);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML,html) ?? '';
  }

  updateImageStyles(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const images = tempDiv.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      img.classList.add('print-image');
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.setAttribute('style', 'max-width: 100%;');
    });

    return tempDiv.innerHTML;
  }

  scrollToSection(id: string, tag: string) {
    const query = `[id='${tag}-${id}']`;
    const element = this.summary.nativeElement.querySelector(query);
    if (element) {
      element.scrollIntoView();
    }
  }
}
